import React, { Component } from 'react'
import { Container, Row, Col } from 'reactstrap'
import ReturnsList from "../CA/ReturnsList"
import '../CA/CarsList.css'
import RentalsListHeader from "../Rentals/RentalListNew/RentalsListHeader"
import ListingTabs from "../Material/ListingTabs"
import { buildPresets } from "../Filters/presetFilters"
import { FILTER_PARAM } from "../Filters/filterUrl"

import withApolloClient from '../withApolloClient'

// Each tab's default filters, expressed as Advanced Filters presets -- the same
// FilterInput[] shape the query already sends for user filters, so the popover,
// the chips and Reset all read one source of truth. See IncidentsListContainer
// for the pattern this follows.
//
// Every one of them is locked (editable: false). These are not defaults the user
// is meant to adjust -- they are what each tab IS: Awaiting To Closeout is stage
// 3, Incomplete Returns is stage 5 restricted to Returns and Switches, Lost is
// return_type Lost. Each renders as a chip with a lock icon and a disabled row
// in the popover, so the user can see the scoping without being able to change
// or drop it. Users narrow further with their own filters instead.
//
// fieldName is "stage", not "return_stage": CarReturn has both fields, but the
// legacy returnStage resolver argument has always filtered stage__in (see
// resolve_all_returns in cars/schema.py) -- return_stage instead records the
// *car's* stage on return day. Using it here would silently filter the wrong
// column.
//
// Enum values are strings ("3", not 3): enum options are advertised as strings
// (FilterFieldOptionType.value is a graphene.String()), and that is what the
// chip label and the popover select look values up by. See sameOptionValue in
// Filters/AdvancedFilters.js -- the backend coerces either form, so a number
// would still filter correctly, it would just render the chip as a bare "3".
export const RETURN_LISTING_TABS = [
    {
        id: "#awaitingToCloseout", name: "Awaiting To Closeout", iconClassName: "fa fa-reply",
        orderBy: ["-agreement__driver__in_office", "-id"],
        getPresetFilters: () => [
            { fieldName: "stage", filterType: "in", filterValues: ["3"], editable: false },
        ],
    },
    {
        id: "#incompleteReturns", name: "Incomplete Returns", iconClassName: "fa fa-hourglass-half",
        orderBy: ["dateAdded", "-id"],
        getPresetFilters: () => [
            { fieldName: "stage", filterType: "in", filterValues: ["5"], editable: false },
            { fieldName: "return_type", filterType: "in", filterValues: ["Return", "Switch"], editable: false },
        ],
    },
    {
        id: "#lost", name: "Lost", iconClassName: "fa fa-times",
        orderBy: ["dateAdded", "-id"],
        getPresetFilters: () => [
            { fieldName: "return_type", filterType: "equal", filterValues: "Lost", editable: false },
        ],
    },
    {
        id: "#all", name: "All Returns", iconClassName: "fa fa-calendar-o",
        orderBy: ["-dateAdded", "-agreement__driver__in_office"],
        getPresetFilters: () => [],
    },
]

export const getPresetsForTab = (tabId) =>
    buildPresets(RETURN_LISTING_TABS.find(tab => tab.id === tabId)?.getPresetFilters() || [])

// allReturns still takes the legacy scalar arguments (returnStage, returnType,
// returnTypeIn) alongside the generic `filters` argument -- see
// resolve_all_returns in cars/schema.py. ReturnsList.js now combines this
// container's `presets` prop with the URL's advancedFilters into `filters`
// (see combineFilters in Filters/presetFilters and ReturnsList.js), so the
// active tab's presets reach the query that way. No frontend caller sends the
// scalar args any more -- Available Cars / Scheduled Returns
// (frontend/src/CA/AvailableCars) was the last one, and it now expresses its
// returnStage/agreementStage defaults as presets too.
class CarReturnListContainer extends Component {
    constructor(props) {
        super(props)
        // presets and defaultPresets start out equal -- setPresets only ever
        // updates presets, so defaultPresets stays the active tab's declared
        // set and is what Reset returns to.
        const initialPresets = getPresetsForTab(RETURN_LISTING_TABS[0].id)
        this.state = {
            activeTab: null,
            searchTerm: "",
            selectedIds: [],
            filterValues: {
                orderBy: RETURN_LISTING_TABS[0].orderBy,
            },
            presets: initialPresets,
            defaultPresets: initialPresets,
            refetchReservations: false,
        }
    }
    updateOrder = (string) => {
        let order = [...this.state.filterValues.orderBy]
        if (!order.includes(string) && !order.includes("-" + string)) {
            order = [string, order.find(item => item.includes("-agreement__driver__in_office"))]
        } else {
            let index = order.indexOf(string)
            if (order.indexOf(string) > -1) {
                order[index] = "-" + string
            } else {
                index = order.indexOf("-" + string)
                index > -1 && (order[index] = string)
            }
        }
        this.setState({ filterValues: { ...this.state.filterValues, orderBy: order } })
    }
    updateSelection = (selection) => {
        if (Array.isArray(selection)) {
            this.setState({ selectedIds: selection })
        } else {
            if (this.state.selectedIds.includes(selection))
                this.setState({ selectedIds: this.state.selectedIds.filter(item => item != selection) })
            else
                this.setState({ selectedIds: [...this.state.selectedIds, selection] })
        }
    }
    setPresets = (presets) => this.setState({ presets })

    // Writes the tab hash through navigate() (path + search + hash together) instead
    // of `window.location.hash = …`. The direct write left react-router's own
    // location.hash -- read by useLocation() everywhere, including
    // Filters/filterUrl.js -- stale, and it never touched `?filter=`.
    //
    // `?filter=` is one shared query param, and all four tabs here list the same
    // `car_return` entity, so a filter applied on one tab stayed in the URL and
    // applied cleanly on the next: the user switched tabs and silently got a
    // narrowed list they never filtered. Clearing it on every switch is the same
    // fix as AvailableCars/AvailableCarsContainer.js's navigateToTab.
    //
    // Mount is the exception (clearFilters: false). Because every tab is the same
    // entity, a shared link's filters are always valid here -- stripping them on
    // load would break filtered links outright, which is not the problem being
    // solved.
    navigateToTab = (tab, { replace = false, clearFilters = true } = {}) => {
        const params = new URLSearchParams(this.props.location.search)
        if (clearFilters) params.delete(FILTER_PARAM)
        this.props.navigate(
            { pathname: this.props.location.pathname, search: params.toString(), hash: tab.id },
            { replace }
        )
    }

    componentDidMount() {
        if (window.location.hash && RETURN_LISTING_TABS.map(item => item.id).includes(window.location.hash)) {
            let tab = RETURN_LISTING_TABS.find(tab => tab.id === window.location.hash)
            const presets = getPresetsForTab(tab.id)
            this.setState({ filterValues: { orderBy: tab.orderBy }, activeTab: tab.name, presets, defaultPresets: presets })
            this.navigateToTab(tab, { replace: true, clearFilters: false })
        } else {
            this.setState({ filterValues: { orderBy: RETURN_LISTING_TABS[0].orderBy }, activeTab: RETURN_LISTING_TABS[0].name })
            this.navigateToTab(RETURN_LISTING_TABS[0], { replace: true, clearFilters: false })
        }
    }
    changeTab = (value) => {
        let tab = RETURN_LISTING_TABS.find(tab => tab.name === value)
        const presets = getPresetsForTab(tab.id)
        this.setState({ activeTab: value, filterValues: { orderBy: tab.orderBy }, presets, defaultPresets: presets })
        this.navigateToTab(tab)
    }
    render() {
        return (
            <Container fluid className="CarListContainer">
                <ListingTabs
                    tabs={RETURN_LISTING_TABS}
                    activeTab={this.state.activeTab}
                    activeValueKey="name"
                    onChange={this.changeTab}
                />
                <RentalsListHeader
                    activeTab={this.state.activeTab}
                    filterValues={this.state.filterValues}
                    setFilterValues={(filterValues) => this.setState({ filterValues })}
                    location={this.props.location} history={this.props.navigate}
                    presets={this.state.presets}
                    setPresets={this.setPresets}
                    defaultPresets={this.state.defaultPresets}
                    selectedDrivers={this.state.selectedIds}
                    resetSelectedDrivers={() => this.setState({ selectedIds: [] })}
                />

                <Row className="pb-2">
                    <Col sm="12">
                        {RETURN_LISTING_TABS.map(tab => tab.name).includes(this.state.activeTab) &&
                            <ReturnsList
                                currentUser={this.props.currentUser}
                                searchTerm={this.state.filterValues && this.state.filterValues.searchTerm ? this.state.filterValues.searchTerm : null}
                                selectedDrivers={this.state.selectedIds}
                                updateDriversSelection={this.updateSelection}
                                orderBy={this.state.filterValues && this.state.filterValues.orderBy ? this.state.filterValues.orderBy : null}
                                presets={this.state.presets}
                                updateOrder={this.updateOrder}
                                activeTab={this.state.activeTab ? this.state.activeTab : ""}
                            />
                        }
                    </Col>
                </Row>
            </Container>
        )
    }
}


export default (withApolloClient(CarReturnListContainer))
